import type { Address, Billing, Cents, ContractTerm, Dealer, Iso } from "./index.js";
import {
  onebillCodeStem,
  productKey,
  quotableBlockers,
  type CatalogProductV2,
  type QuotableBlocker,
} from "./catalog-v2.js";
import type { IntakeFormat } from "./intake-formats.js";
import {
  companyKey,
  exactName,
  normalizePhone10,
  scoreNameSimilarity,
} from "./customers-v2.js";

import type { Via } from "./via.js";

// ---------- Intake Extraction Shape (Model Output) ----------

export type IntakeDocumentKind =
  | "quote"
  | "proposal"
  | "order"
  | "invoice"
  | "other";

export interface IntakeCustomerAddress {
  line1: string | null;
  line2: string | null;
  city: string | null;
  state: string | null;
  postalCode: string | null;
}

export interface IntakeCustomer {
  name: string | null;
  phone: string | null;
  address: IntakeCustomerAddress | null;
}

export interface IntakeContact {
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  phone: string | null;
  title: string | null;
  role: "primary" | "billing" | "other" | null;
}

export interface IntakeTerm {
  months: 12 | 24 | 36 | 60 | null;
  monthToMonth: boolean;
  text: string | null;
}

export interface IntakeLine {
  page: number;
  part: number;
  text: string;
  code: string | null;
  description: string;
  qty: string | null;
  unitPrice: string | null;
  extended: string | null;
  billing: "monthly" | "one_time" | "unknown";
}

export interface IntakeTotals {
  monthly: string | null;
  oneTime: string | null;
}

export interface IntakeExtraction {
  documentKind: IntakeDocumentKind;
  dealerQuoteNumber: string | null;
  quoteDate: string | null;
  customer: IntakeCustomer;
  contacts: IntakeContact[];
  term: IntakeTerm;
  lines: IntakeLine[];
  totals: IntakeTotals;
}

// ---------- Intake Extraction JSON Schema ----------

export const INTAKE_EXTRACTION_TOOL_NAME = "record_quote_document" as const;

export interface AnthropicToolDefinition {
  name: string;
  description: string;
  input_schema: {
    type: "object";
    properties: Record<string, unknown>;
    required: string[];
  };
}

export const INTAKE_EXTRACTION_JSON_PROPERTIES = {
  documentKind: {
    type: "string",
    enum: ["quote", "proposal", "order", "invoice", "other"],
    description: "Kind of sales or quote document transcribed.",
  },
  dealerQuoteNumber: {
    type: ["string", "null"],
    description: "Quote, proposal, or document identifier printed on the page, or null.",
  },
  quoteDate: {
    type: ["string", "null"],
    description: "Quote date as printed (e.g. YYYY-MM-DD or printed string), or null.",
  },
  customer: {
    type: "object",
    additionalProperties: false,
    description: "The prospective or existing customer organization details.",
    required: ["name", "phone", "address"],
    properties: {
      name: {
        type: ["string", "null"],
        description: "Customer or company name as printed, or null.",
      },
      phone: {
        type: ["string", "null"],
        description: "Customer main telephone number as printed, or null.",
      },
      address: {
        type: ["object", "null"],
        additionalProperties: false,
        description: "Customer physical or billing address as printed, or null.",
        required: ["line1", "line2", "city", "state", "postalCode"],
        properties: {
          line1: {
            type: "string",
            description: "Customer street address line 1.",
          },
          line2: {
            type: ["string", "null"],
            description: "Customer street address line 2 or suite, or null.",
          },
          city: {
            type: "string",
            description: "Customer city.",
          },
          state: {
            type: "string",
            description: "Customer two-letter state or province.",
          },
          postalCode: {
            type: "string",
            description: "Customer ZIP or postal code.",
          },
        },
      },
    },
  },
  contacts: {
    type: "array",
    description: "Named contacts and representatives found on the document.",
    items: {
      type: "object",
      additionalProperties: false,
      required: ["firstName", "lastName", "email", "phone", "title", "role"],
      properties: {
        firstName: {
          type: "string",
          description: "Contact first name.",
        },
        lastName: {
          type: "string",
          description: "Contact last name.",
        },
        email: {
          type: "string",
          description: "Contact email address.",
        },
        phone: {
          type: "string",
          description: "Contact direct phone number.",
        },
        title: {
          type: "string",
          description: "Contact job title.",
        },
        role: {
          anyOf: [
            { type: "string", enum: ["primary", "billing", "other"] },
            { type: "null" },
          ],
          description: "Role of the contact if indicated, or null.",
        },
      },
    },
  },
  term: {
    type: "object",
    additionalProperties: false,
    description: "Contract commitment term.",
    required: ["months", "monthToMonth", "text"],
    properties: {
      months: {
        anyOf: [
          { type: "integer", enum: [12, 24, 36, 60] },
          { type: "null" },
        ],
        description: "Term commitment in months (12, 24, 36, 60), or null.",
      },
      monthToMonth: {
        type: "boolean",
        description: "True if month-to-month commitment is indicated.",
      },
      text: {
        type: ["string", "null"],
        description: "Verbatim contract term text as printed, or null.",
      },
    },
  },
  lines: {
    type: "array",
    description: "Line items, charges, services, and hardware listed on the document.",
    items: {
      type: "object",
      additionalProperties: false,
      required: [
        "part",
        "page",
        "text",
        "code",
        "description",
        "qty",
        "unitPrice",
        "extended",
        "billing",
      ],
      properties: {
        part: {
          type: "integer",
          description: "1-based part number, as labelled, where the line appears.",
        },
        page: {
          type: "integer",
          description: "1-based page within a PDF part; 1 for any other part.",
        },
        text: {
          type: "string",
          description: "Verbatim line text as printed.",
        },
        code: {
          type: ["string", "null"],
          description: "Printed part, retail, or product code, or null.",
        },
        description: {
          type: "string",
          description: "Printed product or charge description.",
        },
        qty: {
          type: ["string", "null"],
          description: "Printed unit quantity string, or null.",
        },
        unitPrice: {
          type: ["string", "null"],
          description: "Printed unit price string, or null.",
        },
        extended: {
          type: ["string", "null"],
          description: "Printed extended total string, or null.",
        },
        billing: {
          type: "string",
          enum: ["monthly", "one_time", "unknown"],
          description: "Billing periodicity indicated for this line.",
        },
      },
    },
  },
  totals: {
    type: "object",
    additionalProperties: false,
    description: "Printed summary totals.",
    required: ["monthly", "oneTime"],
    properties: {
      monthly: {
        type: ["string", "null"],
        description: "Printed recurring or monthly total, or null.",
      },
      oneTime: {
        type: ["string", "null"],
        description: "Printed one-time or installation total, or null.",
      },
    },
  },
} as const;

export interface IntakeExtractionJsonSchema {
  type: "object";
  additionalProperties: false;
  description: string;
  required: string[];
  properties: typeof INTAKE_EXTRACTION_JSON_PROPERTIES;
}

export function generateIntakeExtractionJsonSchema(): IntakeExtractionJsonSchema {
  return {
    type: "object",
    additionalProperties: false,
    description:
      "Record the transcribed contents of a quote, proposal, order, or invoice document. Transcribe verbatim; Omni performs catalog and customer matching.",
    required: Object.keys(INTAKE_EXTRACTION_JSON_PROPERTIES),
    properties: INTAKE_EXTRACTION_JSON_PROPERTIES,
  };
}

/**
 * Legacy tool schema wrapper for backward compatibility.
 */
export function generateIntakeExtractionToolSchema(): AnthropicToolDefinition {
  const schema = generateIntakeExtractionJsonSchema();
  return {
    name: INTAKE_EXTRACTION_TOOL_NAME,
    description: schema.description,
    input_schema: {
      type: "object",
      properties: schema.properties as unknown as Record<string, unknown>,
      required: schema.required,
    },
  };
}

export function validateIntakeExtraction(
  input: unknown,
): { ok: true; data: IntakeExtraction } | { ok: false; error: string } {
  if (!input || typeof input !== "object") {
    return { ok: false, error: "Extraction must be an object." };
  }
  const obj = input as Record<string, unknown>;

  const validKinds: IntakeDocumentKind[] = [
    "quote",
    "proposal",
    "order",
    "invoice",
    "other",
  ];
  if (!validKinds.includes(obj.documentKind as IntakeDocumentKind)) {
    return { ok: false, error: "Invalid documentKind." };
  }

  if (!obj.customer || typeof obj.customer !== "object") {
    return { ok: false, error: "Customer must be an object." };
  }
  const cust = obj.customer as Record<string, unknown>;
  const custAddress =
    cust.address && typeof cust.address === "object"
      ? (cust.address as Record<string, unknown>)
      : null;

  const customer: IntakeCustomer = {
    name: typeof cust.name === "string" ? cust.name : null,
    phone: typeof cust.phone === "string" ? cust.phone : null,
    address: custAddress
      ? {
          line1: typeof custAddress.line1 === "string" ? custAddress.line1 : null,
          line2: typeof custAddress.line2 === "string" ? custAddress.line2 : null,
          city: typeof custAddress.city === "string" ? custAddress.city : null,
          state: typeof custAddress.state === "string" ? custAddress.state : null,
          postalCode:
            typeof custAddress.postalCode === "string"
              ? custAddress.postalCode
              : null,
        }
      : null,
  };

  const contacts: IntakeContact[] = [];
  if (Array.isArray(obj.contacts)) {
    for (const c of obj.contacts) {
      if (c && typeof c === "object") {
        const co = c as Record<string, unknown>;
        const role =
          co.role === "primary" || co.role === "billing" || co.role === "other"
            ? co.role
            : null;
        contacts.push({
          firstName: typeof co.firstName === "string" ? co.firstName : null,
          lastName: typeof co.lastName === "string" ? co.lastName : null,
          email: typeof co.email === "string" ? co.email : null,
          phone: typeof co.phone === "string" ? co.phone : null,
          title: typeof co.title === "string" ? co.title : null,
          role,
        });
      }
    }
  }

  const termObj =
    obj.term && typeof obj.term === "object"
      ? (obj.term as Record<string, unknown>)
      : {};
  const months = [12, 24, 36, 60].includes(termObj.months as number)
    ? (termObj.months as 12 | 24 | 36 | 60)
    : null;
  const term: IntakeTerm = {
    months,
    monthToMonth: Boolean(termObj.monthToMonth),
    text: typeof termObj.text === "string" ? termObj.text : null,
  };

  if (!Array.isArray(obj.lines)) {
    return { ok: false, error: "Lines must be an array." };
  }
  const lines: IntakeLine[] = [];
  for (const l of obj.lines) {
    if (!l || typeof l !== "object") continue;
    const lo = l as Record<string, unknown>;
    const billing =
      lo.billing === "monthly" || lo.billing === "one_time"
        ? lo.billing
        : "unknown";
    const part = typeof lo.part === "number" && Number.isInteger(lo.part) && lo.part >= 1 ? lo.part : 1;
    lines.push({
      page: typeof lo.page === "number" ? lo.page : 1,
      part,
      text: typeof lo.text === "string" ? lo.text : "",
      code: typeof lo.code === "string" ? lo.code : null,
      description: typeof lo.description === "string" ? lo.description : "",
      qty: typeof lo.qty === "string" ? lo.qty : null,
      unitPrice: typeof lo.unitPrice === "string" ? lo.unitPrice : null,
      extended: typeof lo.extended === "string" ? lo.extended : null,
      billing,
    });
  }

  const totalsObj =
    obj.totals && typeof obj.totals === "object"
      ? (obj.totals as Record<string, unknown>)
      : {};
  const totals: IntakeTotals = {
    monthly: typeof totalsObj.monthly === "string" ? totalsObj.monthly : null,
    oneTime: typeof totalsObj.oneTime === "string" ? totalsObj.oneTime : null,
  };

  return {
    ok: true,
    data: {
      documentKind: obj.documentKind as IntakeDocumentKind,
      dealerQuoteNumber:
        typeof obj.dealerQuoteNumber === "string"
          ? obj.dealerQuoteNumber
          : null,
      quoteDate: typeof obj.quoteDate === "string" ? obj.quoteDate : null,
      customer,
      contacts,
      term,
      lines,
      totals,
    },
  };
}

// ---------- Money Parsing ----------

export interface ParsedPrintedMoney {
  cents: Cents;
  billingHint: "monthly" | "one_time" | null;
}

const MONTHLY_HINT_REGEX =
  /(?:\/\s*mo(?:nth)?\b|\/\s*m\b|\bper\s+(?:month|mo)\b|\ba\s+month\b|\bmonthly\b)/i;
const ONE_TIME_HINT_REGEX =
  /(?:\/\s*(?:one[ -]?time|once|ea|each)\b|\bone[ -]?time\b|\bonce\b|\bnon[ -]?recurring\b)/i;

/**
 * Parses printed currency strings (e.g. "$1,234.50", "1234.5", "$12/mo") to integer cents
 * and detects any printed billing periodicity hint. Refuses non-money garbage by returning null.
 */
export function parsePrintedMoney(
  input: string | null | undefined,
): ParsedPrintedMoney | null {
  if (typeof input !== "string") return null;
  const raw = input.trim();
  if (!raw) return null;

  let billingHint: "monthly" | "one_time" | null = null;
  if (MONTHLY_HINT_REGEX.test(raw)) {
    billingHint = "monthly";
  } else if (ONE_TIME_HINT_REGEX.test(raw)) {
    billingHint = "one_time";
  }

  // Remove billing hint text
  let s = raw.replace(MONTHLY_HINT_REGEX, "").replace(ONE_TIME_HINT_REGEX, "").trim();

  // Remove currency symbols, common words, and surrounding whitespace
  s = s.replace(/^\s*USD\s*/i, "").replace(/\s*USD\s*$/i, "").trim();

  // Handle accounting parentheses for negative numbers: e.g. ($1,234.50)
  let isNegative = false;
  if (s.startsWith("(") && s.endsWith(")")) {
    isNegative = true;
    s = s.slice(1, -1).trim();
  }

  // Remove leading dollar sign or minus sign in either order
  if (s.startsWith("-")) {
    isNegative = true;
    s = s.slice(1).trim();
  }
  if (s.startsWith("$")) {
    s = s.slice(1).trim();
  }
  if (!isNegative && s.startsWith("-")) {
    isNegative = true;
    s = s.slice(1).trim();
  }

  if (!s) return null;

  // Validate number format: digits with optional valid comma grouping and optional decimal
  // Valid comma grouping: 1 to 3 digits followed by groups of exactly 3 digits
  const hasComma = s.includes(",");
  const hasDot = s.includes(".");

  if (hasComma) {
    const commaParts = s.split(".");
    if (commaParts.length > 2) return null; // multiple decimal points
    const integerPart = commaParts[0]!;
    if (!/^\d{1,3}(,\d{3})+$/.test(integerPart)) {
      return null;
    }
  }

  // Remove commas
  const cleanNumber = s.replace(/,/g, "");

  // Must match standard float format now
  if (!/^\d*(?:\.\d+)?$/.test(cleanNumber) || cleanNumber === "" || cleanNumber === ".") {
    return null;
  }

  const parts = cleanNumber.split(".");
  const wholeStr = parts[0] || "0";
  const whole = parseInt(wholeStr, 10);
  if (Number.isNaN(whole)) return null;

  let centsFraction = 0;
  if (hasDot && parts[1] !== undefined) {
    const dec = parts[1];
    if (dec.length === 0) {
      centsFraction = 0;
    } else if (dec.length === 1) {
      centsFraction = parseInt(dec, 10) * 10;
    } else if (dec.length === 2) {
      centsFraction = parseInt(dec, 10);
    } else {
      // 3 or more decimal places: round to nearest cent
      centsFraction = Math.round(parseFloat(`0.${dec}`) * 100);
    }
  }

  const totalCents = whole * 100 + centsFraction;
  const finalCents = isNegative ? -totalCents : totalCents;

  return {
    cents: finalCents,
    billingHint,
  };
}

// ---------- Quote Intake Line Matching ----------

export interface IntakeProductCandidate {
  id: string;
  name: string;
  code?: string | null;
  listPrice: Cents;
  billing?: Billing | null;
  onebill?: { productCode: string } | null;
  quotable: boolean;
}

export function intakeCandidatesFromCatalog(
  products: readonly CatalogProductV2[],
  dealer: Pick<Dealer, "model">,
): IntakeProductCandidate[] {
  return products.map((p) => ({
    id: p.id,
    name: p.name,
    code: p.code || null,
    listPrice: p.listPrice,
    billing: p.billing,
    onebill: p.onebill ? { productCode: p.onebill.productCode } : null,
    quotable: quotableBlockers(p, dealer).length === 0,
  }));
}

export interface IntakeCodeMatch {
  code: string;
  outcome: "matched" | "ambiguous" | "none";
  productIds: string[];
}

export interface QuoteIntakeLineMatch {
  index: number;
  state: "matched" | "unmatched";
  productId: string | null;
  why: "code" | "name" | "product_key" | null;
  codeMatch?: IntakeCodeMatch | null;
  suggestions: Array<{ productId: string; score: number }>;
  qty: number | null;
  unitPrice: Cents | null;
  billingDiffers: boolean;
}

type IntakeMatchWhy = "code" | "name" | "product_key";

function rankSuggestions(
  printedDesc: string,
  candidates: IntakeProductCandidate[],
  excludeId: string | null,
): Array<{ productId: string; score: number }> {
  if (!printedDesc) return [];
  const scored: Array<{ productId: string; name: string; score: number }> = [];
  for (const c of candidates) {
    if (excludeId && c.id === excludeId) continue;
    const score = scoreNameSimilarity(printedDesc, c.name);
    if (score >= 0.5) {
      scored.push({ productId: c.id, name: c.name, score: Number(score.toFixed(3)) });
    }
  }
  scored.sort((a, b) => b.score - a.score || a.name.localeCompare(b.name));
  return scored.slice(0, 10).map(({ productId, score }) => ({ productId, score }));
}

/**
 * matchIntakeLine per Section 4 rules:
 * 1. Let C be onebillCodeStem(line.code). Strip a printed OneBill suffix so invoices match.
 * 2. Code tier, in this order: quotable products, then the rest. Within each pool:
 *    - first, products whose own code equals C;
 *    - then, products whose linked OneBill code stems to C.
 * 3. In either sub-tier:
 *    - one hit -> matched, why "code";
 *    - several hits -> narrow to the one whose exactName equals the line description; none or several left -> ambiguous;
 *    - ambiguous -> stop: unmatched, with the same-code products first among the suggestions (score 1).
 * 4. Name tiers (exact, then product key; quotable first) run only after the code tier missed. When the line has a code, they skip products whose code is set and differs from it.
 * 5. Lines without a code behave as today.
 */
export function matchIntakeLine(
  line: {
    code?: string | null;
    description: string;
    text?: string;
    qty?: string | null;
    unitPrice?: string | null;
    billing?: "monthly" | "one_time" | "unknown";
  },
  candidates: IntakeProductCandidate[],
  index = 0,
): QuoteIntakeLineMatch {
  const rawPrintedCode = line.code ? line.code.trim() : "";
  const C = onebillCodeStem(rawPrintedCode);
  const CUpper = C.toUpperCase();
  const printedDesc = line.description ? line.description.trim() : (line.text ? line.text.trim() : "");
  const normDesc = exactName(printedDesc);

  let parsedQty: number | null = null;
  if (line.qty) {
    const q = parseInt(line.qty.replace(/[^0-9]/g, ""), 10);
    if (!Number.isNaN(q) && q > 0) parsedQty = q;
  }

  const parsedPrice = line.unitPrice ? parsePrintedMoney(line.unitPrice) : null;
  const printedPriceCents = parsedPrice ? parsedPrice.cents : null;
  const quotable = candidates.filter((c) => c.quotable);
  const rest = candidates.filter((c) => !c.quotable);

  let codeMatch: IntakeCodeMatch | null = null;

  if (C) {
    // Code tier in order: quotable products, then rest
    const pools = [quotable, rest];
    let codeTierHandled = false;

    for (const pool of pools) {
      if (codeTierHandled) break;

      // Sub-tier 1: own code equals C
      const ownCodeMatches = pool.filter((c) => {
        const cCode = c.code ? c.code.trim().toUpperCase() : "";
        return cCode === CUpper;
      });

      // Sub-tier 2: linked OneBill code stems to C
      const onebillStemMatches = pool.filter((c) => {
        const obStem = c.onebill?.productCode
          ? onebillCodeStem(c.onebill.productCode).toUpperCase()
          : "";
        return obStem === CUpper;
      });

      const subTiers = [
        ownCodeMatches,
        // If sub-tier 1 was empty, consider sub-tier 2
        ownCodeMatches.length === 0 ? onebillStemMatches : [],
      ];

      for (const hits of subTiers) {
        if (hits.length === 0) continue;
        codeTierHandled = true;

        if (hits.length === 1) {
          const hitCandidate = hits[0]!;
          codeMatch = {
            code: C,
            outcome: "matched",
            productIds: [hitCandidate.id],
          };
          const printedBilling =
            (line.billing && line.billing !== "unknown" ? line.billing : null) ??
            parsedPrice?.billingHint ??
            null;
          const billingDiffers = Boolean(
            printedBilling && hitCandidate.billing && printedBilling !== hitCandidate.billing,
          );
          return {
            index,
            state: "matched",
            productId: hitCandidate.id,
            why: "code",
            codeMatch,
            suggestions: rankSuggestions(printedDesc, candidates, hitCandidate.id),
            qty: parsedQty,
            unitPrice: printedPriceCents !== null ? printedPriceCents : hitCandidate.listPrice,
            billingDiffers,
          };
        }

        // Several hits: narrow to the one whose exactName equals the line description
        const exactNameHits = normDesc ? hits.filter((c) => exactName(c.name) === normDesc) : [];
        if (exactNameHits.length === 1) {
          const hitCandidate = exactNameHits[0]!;
          codeMatch = {
            code: C,
            outcome: "matched",
            productIds: hits.map((c) => c.id),
          };
          const printedBilling =
            (line.billing && line.billing !== "unknown" ? line.billing : null) ??
            parsedPrice?.billingHint ??
            null;
          const billingDiffers = Boolean(
            printedBilling && hitCandidate.billing && printedBilling !== hitCandidate.billing,
          );
          return {
            index,
            state: "matched",
            productId: hitCandidate.id,
            why: "code",
            codeMatch,
            suggestions: rankSuggestions(printedDesc, candidates, hitCandidate.id),
            qty: parsedQty,
            unitPrice: printedPriceCents !== null ? printedPriceCents : hitCandidate.listPrice,
            billingDiffers,
          };
        }

        // none or several left -> ambiguous; stop: unmatched, with the same-code products first among the suggestions (score 1)
        codeMatch = {
          code: C,
          outcome: "ambiguous",
          productIds: hits.map((c) => c.id),
        };
        const sameCodeSuggestions = hits.map((c) => ({ productId: c.id, score: 1 }));
        const otherSuggestions = rankSuggestions(printedDesc, candidates, null)
          .filter((s) => !hits.some((h) => h.id === s.productId));
        return {
          index,
          state: "unmatched",
          productId: null,
          why: null,
          codeMatch,
          suggestions: [...sameCodeSuggestions, ...otherSuggestions].slice(0, 10),
          qty: parsedQty,
          unitPrice: printedPriceCents,
          billingDiffers: false,
        };
      }
    }

    // Code tier missed
    codeMatch = {
      code: C,
      outcome: "none",
      productIds: [],
    };
  }

  // Name tiers (exact, then product key; quotable first) run only after code tier missed (or line has no code).
  // When the line has a code, they skip products whose code is set and differs from it.
  const filterForNameTiers = (pool: IntakeProductCandidate[]): IntakeProductCandidate[] => {
    if (!C) return pool;
    return pool.filter((c) => {
      const cCode = c.code ? c.code.trim().toUpperCase() : "";
      if (!cCode) return true; // uncoded product
      return cCode === CUpper;
    });
  };

  const eligibleQuotable = filterForNameTiers(quotable);
  const eligibleRest = filterForNameTiers(rest);

  function nameTierMatch(
    pool: IntakeProductCandidate[],
  ): { candidate: IntakeProductCandidate; why: "name" | "product_key" } | "ambiguous" | "none" {
    if (printedDesc) {
      if (normDesc) {
        const nameMatches = pool.filter((c) => exactName(c.name) === normDesc);
        if (nameMatches.length === 1) return { candidate: nameMatches[0]!, why: "name" };
        if (nameMatches.length > 1) return "ambiguous";
      }
      const key = productKey(printedDesc);
      if (key) {
        const keyMatches = pool.filter((c) => productKey(c.name) === key);
        if (keyMatches.length === 1) return { candidate: keyMatches[0]!, why: "product_key" };
        if (keyMatches.length > 1) return "ambiguous";
      }
    }
    return "none";
  }

  const passA = nameTierMatch(eligibleQuotable);
  const hit = passA === "none" ? nameTierMatch(eligibleRest) : passA;

  if (hit !== "ambiguous" && hit !== "none") {
    const printedBilling =
      (line.billing && line.billing !== "unknown" ? line.billing : null) ??
      parsedPrice?.billingHint ??
      null;
    const billingDiffers = Boolean(
      printedBilling && hit.candidate.billing && printedBilling !== hit.candidate.billing,
    );
    return {
      index,
      state: "matched",
      productId: hit.candidate.id,
      why: hit.why,
      codeMatch,
      suggestions: rankSuggestions(printedDesc, candidates, hit.candidate.id),
      qty: parsedQty,
      unitPrice: printedPriceCents !== null ? printedPriceCents : hit.candidate.listPrice,
      billingDiffers,
    };
  }

  return {
    index,
    state: "unmatched",
    productId: null,
    why: null,
    codeMatch,
    suggestions: rankSuggestions(printedDesc, candidates, null),
    qty: parsedQty,
    unitPrice: printedPriceCents,
    billingDiffers: false,
  };
}


// ---------- Quote Intake Customer Matching ----------

export interface IntakeCustomerCandidate {
  autotaskCompanyId: number;
  name: string;
  phone?: string | null;
}

export type QuoteIntakeCustomerMatch =
  | {
      kind: "existing";
      autotaskCompanyId: number;
      name: string;
      why: "phone" | "name" | "company_key" | "autotask_id";
    }
  | {
      kind: "new";
      candidates: Array<{
        autotaskCompanyId: number;
        name: string;
        score: number;
      }>;
    };

/**
 * Matches an extracted customer against the dealer's customer list per Section 3.2.
 * Tiers:
 * 1. 10-digit phone
 * 2. Exact name
 * 3. Company key
 * Each certain only on exactly one candidate. Otherwise returns "new" prospect with up to 3 candidates.
 */
export function matchIntakeCustomer(
  extracted: { name?: string | null; phone?: string | null },
  candidates: IntakeCustomerCandidate[],
  hints?: { autotaskCompanyId?: number | null },
): QuoteIntakeCustomerMatch {
  const normPhone = normalizePhone10(extracted.phone);
  const rawName = extracted.name ? extracted.name.trim() : "";
  const hinted = hints?.autotaskCompanyId;
  if (typeof hinted === "number") {
    const idMatches = candidates.filter((c) => c.autotaskCompanyId === hinted);
    if (idMatches.length === 1) {
      return {
        kind: "existing",
        autotaskCompanyId: idMatches[0]!.autotaskCompanyId,
        name: idMatches[0]!.name,
        why: "autotask_id",
      };
    }
    if (idMatches.length > 1) return buildNewCustomerMatch(rawName, candidates, normPhone);
  }


  // Tier 1: 10-digit phone
  if (normPhone) {
    const phoneMatches = candidates.filter(
      (c) => normalizePhone10(c.phone) === normPhone,
    );
    if (phoneMatches.length === 1) {
      return {
        kind: "existing",
        autotaskCompanyId: phoneMatches[0]!.autotaskCompanyId,
        name: phoneMatches[0]!.name,
        why: "phone",
      };
    } else if (phoneMatches.length > 1) {
      // Ambiguous: not certain on one
      return buildNewCustomerMatch(rawName, candidates, normPhone);
    }
  }

  // Tier 2: Exact name
  if (rawName) {
    const normName = exactName(rawName);
    if (normName) {
      const nameMatches = candidates.filter(
        (c) => exactName(c.name) === normName,
      );
      if (nameMatches.length === 1) {
        return {
          kind: "existing",
          autotaskCompanyId: nameMatches[0]!.autotaskCompanyId,
          name: nameMatches[0]!.name,
          why: "name",
        };
      } else if (nameMatches.length > 1) {
        return buildNewCustomerMatch(rawName, candidates);
      }
    }
  }

  // Tier 3: Company key
  if (rawName) {
    const key = companyKey(rawName);
    if (key) {
      const keyMatches = candidates.filter(
        (c) => companyKey(c.name) === key,
      );
      if (keyMatches.length === 1) {
        return {
          kind: "existing",
          autotaskCompanyId: keyMatches[0]!.autotaskCompanyId,
          name: keyMatches[0]!.name,
          why: "company_key",
        };
      }
    }
  }

  return buildNewCustomerMatch(rawName, candidates, normPhone);
}

function buildNewCustomerMatch(
  name: string,
  candidates: IntakeCustomerCandidate[],
  phone?: string | null,
): QuoteIntakeCustomerMatch {
  const scored: Array<{ autotaskCompanyId: number; name: string; score: number }> = [];

  for (const c of candidates) {
    if (phone && normalizePhone10(c.phone) === phone) {
      scored.push({
        autotaskCompanyId: c.autotaskCompanyId,
        name: c.name,
        score: 1.0,
      });
      continue;
    }

    if (name) {
      const score = scoreNameSimilarity(name, c.name);
      if (score >= 0.5) {
        scored.push({
          autotaskCompanyId: c.autotaskCompanyId,
          name: c.name,
          score: Number(score.toFixed(3)),
        });
      }
    }
  }

  scored.sort((a, b) => b.score - a.score || a.name.localeCompare(b.name));
  return {
    kind: "new",
    candidates: scored.slice(0, 3),
  };
}

// ---------- Full Intake Matching Helper ----------

export interface QuoteIntakeTotalsMatch {
  monthlyPrinted: Cents | null;
  monthlyLines: Cents;
  oneTimePrinted: Cents | null;
  oneTimeLines: Cents;
}

export interface QuoteIntakeMatch {
  customer: QuoteIntakeCustomerMatch;
  lines: QuoteIntakeLineMatch[];
  totals: QuoteIntakeTotalsMatch;
}

/**
 * Matches extraction customer, lines, and totals check against catalog and customer candidates.
 */
export function matchIntakeExtraction(
  extraction: IntakeExtraction,
  catalogCandidates: IntakeProductCandidate[],
  customerCandidates: IntakeCustomerCandidate[],
  hints?: { autotaskCompanyId?: number | null },
): QuoteIntakeMatch {
  const customer = matchIntakeCustomer(extraction.customer, customerCandidates, hints);

  const lines = extraction.lines.map((l, idx) =>
    matchIntakeLine(l, catalogCandidates, idx),
  );

  let monthlyLines = 0;
  let oneTimeLines = 0;

  const catalogMap = new Map<string, IntakeProductCandidate>();
  for (const c of catalogCandidates) {
    catalogMap.set(c.id, c);
  }

  for (let i = 0; i < lines.length; i++) {
    const lm = lines[i]!;
    const el = extraction.lines[i];
    const qty = lm.qty ?? 1;
    const price = lm.unitPrice ?? 0;
    const lineTotal = qty * price;

    const matchedProd = lm.productId ? catalogMap.get(lm.productId) : null;
    const billing =
      matchedProd?.billing ??
      (el?.billing === "monthly" || el?.billing === "one_time" ? el.billing : null);

    if (billing === "monthly") {
      monthlyLines += lineTotal;
    } else if (billing === "one_time") {
      oneTimeLines += lineTotal;
    }
  }

  const monthlyParsed = extraction.totals.monthly
    ? parsePrintedMoney(extraction.totals.monthly)
    : null;
  const oneTimeParsed = extraction.totals.oneTime
    ? parsePrintedMoney(extraction.totals.oneTime)
    : null;

  return {
    customer,
    lines,
    totals: {
      monthlyPrinted: monthlyParsed ? monthlyParsed.cents : null,
      monthlyLines,
      oneTimePrinted: oneTimeParsed ? oneTimeParsed.cents : null,
      oneTimeLines,
    },
  };
}

// ---------- Quote Intake Firestore Model ----------

export type QuoteIntakeState =
  | "extracting"
  | "ready"
  | "failed"
  | "confirmed"
  | "discarded";

export type IntakeTextKind = "document" | "transcript";

interface IntakeSourceBlob {
  bytes: number;
  sha256: string;
  gcsPath: string;
}

export type QuoteIntakeSource =
  | (IntakeSourceBlob & { kind: "file"; format: IntakeFormat; mediaType: string; filename: string })
  | (IntakeSourceBlob & { kind: "text"; textKind: IntakeTextKind; title: string | null; chars: number })
  | (IntakeSourceBlob & {
      kind: "kqm";
      quoteId: number;
      quoteNumber: string;
      title: string | null;
      url: string;
      fetchedAt: Iso;
      autotaskCompanyId: number | null;
    });

export type IntakePartKind = "pdf" | "image" | "table" | "text";

export interface IntakePart {
  index: number;
  kind: IntakePartKind;
  label: string;
  mediaType: string;
  bytes: number;
  gcsPath: string;
}

export interface QuoteIntakeExtractionRecord {
  model: string;
  inputTokens: number;
  outputTokens: number;
  ms: number;
  result: IntakeExtraction;
}

export interface QuoteIntakeConfirmed {
  by: string;
  at: Iso;
  customerId: number;
  quoteId: string;
  contactIds: number[];
}

export interface QuoteIntake {
  id: string;
  number: string; // "I-1001"
  dealerId: string;
  createdBy: string;
  createdVia: Via;
  createdAt: Iso;
  updatedAt: Iso;
  source: QuoteIntakeSource;
  parts: IntakePart[] | null;
  state: QuoteIntakeState;
  attempts: number;
  error: string | null;
  extraction: QuoteIntakeExtractionRecord | null;
  match: QuoteIntakeMatch | null;
  confirmed: QuoteIntakeConfirmed | null;
}

// ---------- Intake Confirmation Request ----------

export interface IntakeConfirmCustomerChoice {
  kind: "existing" | "new";
  autotaskCompanyId?: number;
  name?: string;
  phone?: string | null;
  address?: Address | null;
}

export interface IntakeConfirmLineDecision {
  index: number;
  productId?: string | null;
  omit?: boolean;
  qty?: number;
  unitPrice?: Cents;
}

export interface IntakeConfirmContactInput {
  firstName: string;
  lastName: string;
  email?: string | null;
  phone?: string | null;
  title?: string | null;
  role?: "primary" | "billing" | "other" | null;
}

export interface IntakeConfirmRequest {
  customer: IntakeConfirmCustomerChoice;
  contacts?: IntakeConfirmContactInput[];
  /** An existing contact of an existing customer. */
  primaryContactId?: number;
  lines: IntakeConfirmLineDecision[];
  term?: ContractTerm;
  approvalNote?: string;
}

/**
 * Validates that an intake confirmation request is structurally sound and satisfies:
 * - Customer choice is explicit (existing with company ID or new with company name)
 * - Refuses while any unmatched line is neither picked nor left out (omitted)
 */
export function validateIntakeConfirm(
  intake: QuoteIntake,
  request: IntakeConfirmRequest,
): { ok: true } | { ok: false; error: string } {
  if (!request || typeof request !== "object") {
    return { ok: false, error: "Confirmation request must be an object." };
  }

  if (!request.customer || (request.customer.kind !== "existing" && request.customer.kind !== "new")) {
    return { ok: false, error: "Customer choice must be 'existing' or 'new'." };
  }

  if (request.customer.kind === "existing") {
    if (typeof request.customer.autotaskCompanyId !== "number" || request.customer.autotaskCompanyId <= 0) {
      return { ok: false, error: "Existing customer selection requires a valid autotaskCompanyId." };
    }
  } else {
    if (typeof request.customer.name !== "string" || !request.customer.name.trim()) {
      return { ok: false, error: "New customer selection requires a company name." };
    }
  }
  if (typeof request.primaryContactId === "number") {
    if (request.customer.kind !== "existing") {
      return { ok: false, error: "primaryContactId applies only to an existing customer." };
    }
    if ((request.contacts ?? []).some((contact) => contact.role === "primary")) {
      return { ok: false, error: "A new contact cannot be primary when primaryContactId is set." };
    }
  }


  if (!Array.isArray(request.lines)) {
    return { ok: false, error: "Confirmation request requires a lines array." };
  }

  if (!intake.match || !intake.match.lines) {
    return { ok: false, error: "Intake cannot be confirmed before extraction and matching are complete." };
  }

  const decisionMap = new Map<number, IntakeConfirmLineDecision>();
  for (const l of request.lines) {
    decisionMap.set(l.index, l);
  }

  for (const matchLine of intake.match.lines) {
    if (matchLine.state === "unmatched") {
      const decision = decisionMap.get(matchLine.index);
      if (!decision || (!decision.omit && !decision.productId)) {
        return {
          ok: false,
          error: `Unmatched line at index ${matchLine.index} must be mapped to a catalog product or omitted.`,
        };
      }
    }
  }

  return { ok: true };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

export function sourceName(source: QuoteIntakeSource | QuoteIntakeSourceView): string {
  if (source.kind === "file") return source.filename;
  if (source.kind === "kqm") return `KQM quote ${source.quoteNumber}`;
  return source.title ?? (source.textKind === "transcript" ? "Conversation transcript" : "Pasted text");
}

export function intakeReviewUrl(lensBaseUrl: string, id: string): string {
  return `${lensBaseUrl.replace(/\/+$/, "")}/quotes/intake/${id}`;
}

export function intakeConfirmInternalNotes(
  source: QuoteIntakeSource,
  number: string,
  omitted: readonly { label: string }[],
): string {
  const base = `From ${sourceName(source)} (${number}).`;
  if (omitted.length === 0) return base;
  return `${base} Left out: ${omitted.map((item) => item.label).join("; ")}.`;
}

export type QuoteIntakeSourceView =
  | Omit<Extract<QuoteIntakeSource, { kind: "file" }>, "gcsPath">
  | Omit<Extract<QuoteIntakeSource, { kind: "text" }>, "gcsPath">
  | Omit<Extract<QuoteIntakeSource, { kind: "kqm" }>, "gcsPath">;

export interface IntakeProductRef {
  productId: string;
  name: string;
  code: string | null;
  billing: Billing | null;
  listPrice: Cents;
  quotable: boolean;
  blockers: QuotableBlocker[];
}

export interface QuoteIntakeLineView {
  index: number;
  state: "matched" | "switched_off" | "unmatched";
  product: IntakeProductRef | null;
  why: "code" | "name" | "product_key" | null;
  codeMatch: { code: string; outcome: IntakeCodeMatch["outcome"]; products: IntakeProductRef[] } | null;
  suggestions: IntakeProductRef[];
  qty: number | null;
  unitPrice: Cents | null;
  billingDiffers: boolean;
}

export type IntakeAudience = "staff" | "dealer";

export interface QuoteIntakeView {
  id: string;
  number: string;
  dealerId: string;
  createdBy: string;
  createdVia: Via;
  createdAt: Iso;
  updatedAt: Iso;
  state: QuoteIntakeState;
  attempts: number;
  error: string | null;
  source: QuoteIntakeSourceView;
  parts: Array<Omit<IntakePart, "gcsPath">> | null;
  extraction: QuoteIntakeExtractionRecord | null;
  match: { customer: QuoteIntakeCustomerMatch; lines: QuoteIntakeLineView[]; totals: QuoteIntakeTotalsMatch } | null;
  confirmed: QuoteIntakeConfirmed | null;
  reviewUrl: string;
}

export interface QuoteIntakeSummary {
  id: string;
  number: string;
  dealerId: string;
  state: QuoteIntakeState;
  error: string | null;
  createdBy: string;
  createdVia: Via;
  createdAt: Iso;
  updatedAt: Iso;
  confirmed: QuoteIntakeConfirmed | null;
  source: QuoteIntakeSourceView;
  reviewUrl: string;
  lineCount: number | null;
}

function withoutGcsPath<T extends { gcsPath: string }>(value: T): Omit<T, "gcsPath"> {
  const { gcsPath: _gcsPath, ...rest } = value;
  return rest;
}

function toProductRef(product: CatalogProductV2, dealer: { model: Dealer["model"] }): IntakeProductRef {
  const blockers = quotableBlockers(product, dealer);
  return {
    productId: product.id,
    name: product.name,
    code: product.code || null,
    billing: product.billing,
    listPrice: product.listPrice,
    quotable: blockers.length === 0,
    blockers,
  };
}

export function presentIntake(
  intake: QuoteIntake,
  ctx: {
    products: ReadonlyMap<string, CatalogProductV2>;
    dealer: Pick<Dealer, "model">;
    audience: IntakeAudience;
    reviewUrl: string;
  },
): QuoteIntakeView {
  const lines = intake.match?.lines.map((line): QuoteIntakeLineView => {
    const stored = line.productId ? ctx.products.get(line.productId) : undefined;
    const ref = stored ? toProductRef(stored, ctx.dealer) : null;
    const blocked = Boolean(ref && !ref.quotable);
    let state: QuoteIntakeLineView["state"] = "unmatched";
    let product: IntakeProductRef | null = null;
    let why = line.why;
    if (ref && ref.quotable) {
      state = "matched";
      product = ref;
    } else if (ref && blocked && ctx.audience === "staff") {
      state = "switched_off";
      product = ref;
    } else if (ctx.audience === "dealer") {
      why = null;
    }
    const suggestionRefs = line.suggestions.flatMap((suggestion) => {
      const found = ctx.products.get(suggestion.productId);
      return found ? [toProductRef(found, ctx.dealer)] : [];
    });
    const suggestions =
      ctx.audience === "staff"
        ? suggestionRefs.slice(0, 5)
        : suggestionRefs.filter((item) => item.quotable).slice(0, 3);

    let codeMatch: QuoteIntakeLineView["codeMatch"] = null;
    if (line.codeMatch) {
      if (ctx.audience === "staff") {
        const matchedProducts = line.codeMatch.productIds.flatMap((id) => {
          const found = ctx.products.get(id);
          return found ? [toProductRef(found, ctx.dealer)] : [];
        });
        codeMatch = {
          code: line.codeMatch.code,
          outcome: line.codeMatch.outcome,
          products: matchedProducts,
        };
      } else {
        // dealer: products [], outcome "matched" only when the line state is matched, else "none"
        codeMatch = {
          code: line.codeMatch.code,
          outcome: state === "matched" ? "matched" : "none",
          products: [],
        };
      }
    }

    return {
      index: line.index,
      state,
      product,
      why,
      codeMatch,
      suggestions,
      qty: line.qty,
      unitPrice: line.unitPrice,
      billingDiffers: line.billingDiffers,
    };
  });
  return {
    id: intake.id,
    number: intake.number,
    dealerId: intake.dealerId,
    createdBy: intake.createdBy,
    createdVia: intake.createdVia,
    createdAt: intake.createdAt,
    updatedAt: intake.updatedAt,
    state: intake.state,
    attempts: intake.attempts,
    error: intake.error,
    source: withoutGcsPath(intake.source) as QuoteIntakeSourceView,
    parts: intake.parts ? intake.parts.map((part) => withoutGcsPath(part)) : null,
    extraction: intake.extraction,
    match: intake.match && lines
      ? { customer: intake.match.customer, lines, totals: intake.match.totals }
      : null,
    confirmed: intake.confirmed,
    reviewUrl: ctx.reviewUrl,
  };
}

export function summarizeIntake(intake: QuoteIntake, reviewUrl: string): QuoteIntakeSummary {
  return {
    id: intake.id,
    number: intake.number,
    dealerId: intake.dealerId,
    state: intake.state,
    error: intake.error,
    createdBy: intake.createdBy,
    createdVia: intake.createdVia,
    createdAt: intake.createdAt,
    updatedAt: intake.updatedAt,
    confirmed: intake.confirmed,
    source: withoutGcsPath(intake.source) as QuoteIntakeSourceView,
    reviewUrl,
    lineCount: intake.extraction?.result.lines.length ?? intake.match?.lines.length ?? null,
  };
}

export type IntakeConfirmPlan =
  | { ok: true; lines: Array<{ index: number; productId: string; qty: number; unitPrice: Cents }>; omitted: Array<{ index: number; label: string }> }
  | { ok: false; error: string };

export function planIntakeConfirm(
  intake: QuoteIntake,
  request: IntakeConfirmRequest,
  products: ReadonlyMap<string, CatalogProductV2>,
  dealer: Pick<Dealer, "model" | "name">,
): IntakeConfirmPlan {
  if (!intake.match?.lines) {
    return { ok: false, error: "Intake cannot be confirmed before extraction and matching are complete." };
  }
  const decisions = new Map(request.lines.map((line) => [line.index, line]));
  const kept: Array<{ index: number; productId: string; qty: number; unitPrice: Cents }> = [];
  const omitted: Array<{ index: number; label: string }> = [];
  for (const line of intake.match.lines) {
    const decision = decisions.get(line.index);
    if (decision?.omit) {
      const qty = decision.qty ?? line.qty;
      const description =
        intake.extraction?.result.lines[line.index]?.description?.trim() || `Line ${line.index + 1}`;
      omitted.push({
        index: line.index,
        label: typeof qty === "number" ? `${description} x${qty}` : description,
      });
      continue;
    }
    const productId = decision?.productId ?? line.productId;
    const product = productId ? products.get(productId) : undefined;
    if (!product || quotableBlockers(product, dealer).length > 0) {
      return {
        ok: false,
        error: `Line ${line.index + 1} needs a product from ${dealer.name}'s catalog, or leave it out.`,
      };
    }
    const qty = decision?.qty ?? line.qty;
    if (qty === null || qty === undefined || qty <= 0) {
      return { ok: false, error: `Line ${line.index} has no valid quantity.` };
    }
    const unitPrice = decision?.unitPrice ?? line.unitPrice;
    if (unitPrice === null || unitPrice === undefined) {
      return { ok: false, error: `Line ${line.index} has no confirmed unit price.` };
    }
    kept.push({ index: line.index, productId: product.id, qty, unitPrice });
  }
  if (kept.length === 0) {
    return { ok: false, error: "Every line is left out, so there is nothing to quote." };
  }
  return { ok: true, lines: kept, omitted };
}

function upgradeSuggestions(value: unknown): Array<{ productId: string; score: number }> {
  if (!Array.isArray(value)) return [];
  const suggestions: Array<{ productId: string; score: number }> = [];
  for (const item of value) {
    if (!isRecord(item) || typeof item.productId !== "string") continue;
    suggestions.push({
      productId: item.productId,
      score: typeof item.score === "number" ? item.score : 0,
    });
  }
  return suggestions;
}

export function upgradeStoredIntake(doc: unknown): QuoteIntake {
  if (!isRecord(doc) || !isRecord(doc.source)) {
    throw new Error("Stored intake is not an object.");
  }
  if (typeof doc.source.kind === "string") {
    return doc as unknown as QuoteIntake;
  }
  const filename = typeof doc.source.filename === "string" ? doc.source.filename : "quote.pdf";
  const bytes = typeof doc.source.bytes === "number" ? doc.source.bytes : 0;
  const sha256 = typeof doc.source.sha256 === "string" ? doc.source.sha256 : "";
  const gcsPath = typeof doc.source.gcsPath === "string" ? doc.source.gcsPath : "";
  const source: QuoteIntakeSource = {
    kind: "file",
    format: "pdf",
    mediaType: "application/pdf",
    filename,
    bytes,
    sha256,
    gcsPath,
  };
  const parts: IntakePart[] = [
    { index: 0, kind: "pdf", label: filename, mediaType: "application/pdf", bytes, gcsPath },
  ];
  let extraction = doc.extraction as QuoteIntake["extraction"];
  if (isRecord(doc.extraction) && isRecord(doc.extraction.result) && Array.isArray(doc.extraction.result.lines)) {
    extraction = {
      ...(doc.extraction as unknown as QuoteIntakeExtractionRecord),
      result: {
        ...(doc.extraction.result as unknown as IntakeExtraction),
        lines: (doc.extraction.result.lines as unknown[]).map((line) => {
          const record = isRecord(line) ? line : {};
          const part = typeof record.part === "number" && Number.isInteger(record.part) && record.part >= 1 ? record.part : 1;
          return { ...(record as unknown as IntakeLine), part };
        }),
      },
    };
  }
  let match = doc.match as QuoteIntake["match"];
  if (isRecord(doc.match) && Array.isArray(doc.match.lines)) {
    match = {
      ...(doc.match as unknown as QuoteIntakeMatch),
      lines: (doc.match.lines as unknown[]).map((line) => {
        const record = isRecord(line) ? line : {};
        return {
          ...(record as unknown as QuoteIntakeLineMatch),
          suggestions: upgradeSuggestions(record.suggestions),
        };
      }),
    };
  }
  return {
    ...(doc as unknown as QuoteIntake),
    source,
    parts,
    extraction,
    match,
  };
}

// ---------- Route Descriptors ----------

export interface RouteDescriptor {
  method: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  path: string;
  description: string;
}

export const QUOTE_INTAKE_ROUTES: readonly RouteDescriptor[] = [
  { method: "POST", path: "/api/quote-intakes", description: "Upload a quote file (max 10MB)" },
  { method: "POST", path: "/api/quote-intakes/text", description: "Submit pasted quote text or a transcript" },
  { method: "POST", path: "/api/quote-intakes/kqm", description: "Import a KQM quote (Phoneware staff)" },
  { method: "GET", path: "/api/quote-intakes", description: "List quote intakes" },
  { method: "GET", path: "/api/quote-intakes/:id", description: "Get quote intake by ID" },
  { method: "GET", path: "/api/quote-intakes/:id/parts/:index", description: "Read one rendered intake part" },
  { method: "POST", path: "/api/quote-intakes/:id/confirm", description: "Confirm quote intake and create prospect and quote draft" },
  { method: "POST", path: "/api/quote-intakes/:id/discard", description: "Discard quote intake" },
  { method: "POST", path: "/api/internal/intakes/:id/run", description: "Cloud Tasks internal runner to extract and match" },
  { method: "POST", path: "/api/v1/quote-intakes", description: "Submit a quote file via dealer API" },
  { method: "POST", path: "/api/v1/quote-intakes/text", description: "Submit pasted quote text via dealer API" },
  { method: "POST", path: "/api/v1/quote-intakes/kqm", description: "Import a KQM quote via dealer API" },
  { method: "GET", path: "/api/v1/quote-intakes/:id", description: "Get quote intake via dealer API" },
] as const;

